/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { Chart } from 'react-google-charts';
import { CHART_METRICS, getChartColors, getChartDataByValues, isAssuranceRestated } from '../../../utils/charts';
import { UniversalTrackerPlain, UtrValueType } from '../../../types/universalTracker';
import { getUnitText } from '../../../utils/units';
import { DEFAULT_METRIC_UNIT } from '@constants/utr';
import { LoadingPlaceholder } from '@g17eco/molecules/loader-container';
import { UniversalTrackerModalServiceUtrv } from '@g17eco/types/universalTrackerValue';
import UniversalTrackerClassInterface from '../../../model/UniversalTrackerClassInterface';

interface ChartComponentProps {
  selectedColumnCode?: string;
  utrvs: UniversalTrackerModalServiceUtrv[];
  utr: UniversalTrackerClassInterface;
}


type MetricUtrProps = Pick<UniversalTrackerPlain, 'valueType' | 'valueValidation' | 'numberScale'>;

interface MetricUnitDesc {
  /**
   * @deprecated in favour of UniversalTrackerPlain
   */
  (utr: UniversalTrackerClassInterface, selectedColumnCode?: string, fallback?: string): string;

  (utr: MetricUtrProps, selectedColumnCode?: string, fallback?: string): string;
}

const getMetricUnitFromUtr = (utrPlain: MetricUtrProps, fallback?: string) => {
  const unitText = getUnitText(utrPlain, fallback);
  const numberScale = utrPlain.numberScale;
  return numberScale ? `${numberScale} ${unitText}` : unitText;
};

export const getMetricUnitDesc: MetricUnitDesc = (utr, selectedColumnCode, fallback): string => {
  const utrPlain: MetricUtrProps = 'getRaw' in utr ? utr.getRaw() : utr;
  if (selectedColumnCode) {
    if (utrPlain.valueType === UtrValueType.Table) {
      const column = utrPlain.valueValidation?.table?.columns.find((c) => c.code === selectedColumnCode);
      if (column?.validation?.max === 100 && column?.validation.min === 0) {
        return '%';
      }
      if (!column?.unit) {
        return DEFAULT_METRIC_UNIT;
      }
      const unitText = getUnitText(
        {
          valueType: column.type,
          unit: column.unit,
          unitType: column.unitType,
        },
        fallback
      );
      return column.numberScale ? `${column.numberScale} ${unitText}` : unitText;
    }
    if (utrPlain.valueType === UtrValueType.NumericValueList) {
      return getMetricUnitFromUtr(utrPlain, fallback);
    }
    return DEFAULT_METRIC_UNIT;
  }
  return getMetricUnitFromUtr(utrPlain, fallback);
};

export const ChartComponent = (props: ChartComponentProps) => {
  const { selectedColumnCode, utrvs, utr } = props;

  const chartData = getChartDataByValues(utrvs, selectedColumnCode);

  if (!chartData || chartData.length === 0) {
    chartData.push(CHART_METRICS);
  }

  const isRestated = isAssuranceRestated(utrvs);
  const metricUnitText = getMetricUnitDesc(utr, selectedColumnCode);

  return (
    <Chart
      chartType='LineChart'
      data={chartData}
      loader={<LoadingPlaceholder height={400} />}
      options={{
        lineWidth: 2,
        pointSize: 8,
        colors: getChartColors(isRestated),
        chartArea: {
          left: 100,
          top: 20,
          bottom: 80,
          right: 30,
          backgroundColor: {
            fill: '#ffffff',
            strokeWidth: 1
          }
        },
        backgroundColor: 'transparent',
        legend: {
          position: 'none',
        },
        hAxis: {
          gridlines: {
            color: 'transparent',
            minSpacing: 80,
          },
          textStyle: {
            color: '#9ea0a3',
            fontSize: 12
          },
          minorGridlines: {
            minSpacing: 80
          },
          format: 'MMM\ny',
        },
        vAxis: {
          title: metricUnitText,
          minValue: 0,
          gridlines: {
            color: '#e6e9ed',
            minSpacing: 30,
          },
          textStyle: {
            color: '#9ea0a3',
            fontSize: 12
          },
          minorGridlines: {
            minSpacing: 30
          },
          baselineColor: '#6a6c70',
        },
        interpolateNulls: true,
        dataOpacity: 0.8,
        aggregationTarget: 'auto',
        focusTarget: 'category'
      }}
      width='100%'
      height='400px'
    />
  );
}
